import { CSSProperties } from 'react';
import { b2bDark, gxDark, gxLight, b2bLight } from '@mews/design-tokens';

export interface Variable<V> {
    variant?: V;
}

export const SIZES = ['small', 'medium', 'large', 'extraLarge'] as const;
export type Size = (typeof SIZES)[number];

export interface Sized {
    size?: Size;
}

export interface Disableable {
    disabled?: boolean;
}

export type InputValue = string | number;

export interface Minifiable {
    small?: boolean;
}

export enum Status {
    Error = 'error',
    Success = 'success',
}

export interface ClassNamed {
    className?: string;
}

export interface Inlined {
    inline?: boolean;
}

export interface Valued {
    value: string | number;
}

export type PartialDeep<T> = {
    [P in keyof T]?: PartialDeep<T[P]>;
};

export interface Settings {
    rootSize: number;
    componentsProps: ComponentsProps;
}

export type Subtract<T, K> = Omit<T, keyof K>;

export type MaybeHTMLElement = HTMLElement | undefined;
export type MaybeHTMLElementOrNull = MaybeHTMLElement | null;
export type ElementGetter = () => MaybeHTMLElementOrNull;
export type ElementOrElementGetter = MaybeHTMLElementOrNull | ElementGetter;

export type StandartHTMLInputProps = Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'disabled' | 'name' | 'onBlur' | 'onFocus' | 'readOnly' | 'required'
>;

export interface Activable {
    active?: boolean;
}

export type DebouncedFunction<T extends (...args: any[]) => unknown> = T & { cancel: () => void };

export enum DebounceTimeoutValues {
    textInput = 1000,
    Normal = 500,
}

export enum ComponentInteractionState {
    Active = 'active',
    Default = 'default',
    Focus = 'focus',
    Hover = 'hover',
}

export type ComponentTokens<Variant extends string, State extends string, Values extends string = DesignToken> = {
    [property in keyof CSSProperties]: {
        [key in ComponentInteractionState.Default]: Record<Variant, Record<State, Values>>;
    } & {
        [key in Exclude<ComponentInteractionState, ComponentInteractionState.Default>]?: Record<
            Variant,
            Record<State, Values>
        >;
    };
};

export type ThemeVariant = 'b2bLight' | 'b2bDark' | 'gxLight' | 'gxDark';

export type Theme = typeof b2bDark | typeof b2bLight | typeof gxDark | typeof gxLight;

export type DesignToken = keyof typeof b2bLight & keyof typeof b2bDark & keyof typeof gxLight & keyof typeof gxDark;

export interface ComponentsProps {
    Autocomplete: { size: Size };
    Button: { size: Size };
    IconButton: { size: Size };
    ListItem: { dense: boolean };
    Checkbox: { size: Exclude<Size, 'extraLarge'> };
    Select: { size: Size };
    Input: { size: Size };
    Stepper: { size: Size };
    NumberInput: { size: Size };
}
export interface WithChildren {
    children?: React.ReactNode;
}
